import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Card,
    Badge,
    Button,
    Spinner,
    Alert,
    Modal,
    Collapse,
} from 'react-bootstrap';

import InfoTooltip from '@/components/common/InfoTooltip';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';
import {
    dismissReportsByPost,
    getFlaggedPostsForModerator,
    moderatePost,
    REPORT_REASONS,
} from '@/shared/services/reportApi';
import useModeratorCommunities from '@/hooks/useModeratorCommunities';

const FLAGGED_STATUS_LABELS = {
    pending_review: 'Pending review',
    flagged: 'Flagged by AI',
};

const reasonLabel = (value) => {
    const found = REPORT_REASONS.find((r) => r.value === value);
    return found ? found.label : value;
};

const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const truncate = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

export default function ModeratorModeration() {
    const { user } = useAuth();
    const api = useAuthenticatedAxios();
    const { moderatorCommunities, loading: communitiesLoading } =
        useModeratorCommunities();

    const [reportedPosts, setReportedPosts] = useState([]);
    const [flaggedPosts, setFlaggedPosts] = useState([]);
    // Total flagged-post count across all communities the user moderates,
    // before the server-side limit truncates the rendered list. Used to
    // surface "Showing first N of M" guidance when the queue exceeds the
    // single-page cap. Distinct from flaggedPosts.length, which is at most
    // the server cap (100).
    const [flaggedTotal, setFlaggedTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingFlagged, setLoadingFlagged] = useState(true);
    const [error, setError] = useState(null);
    const [flaggedError, setFlaggedError] = useState(null);
    const [actionInProgress, setActionInProgress] = useState(null);
    const [expandedPost, setExpandedPost] = useState(null);
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        postId: null,
        action: null,
        postTitle: '',
    });

    const fetchReports = useCallback(async () => {
        if (!user) return;
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/api/reports/moderator');
            setReportedPosts(response.data);
        } catch (err) {
            console.error('Failed to fetch moderator reports:', err);
            setError(err.response?.data?.message || 'Failed to load reports');
        } finally {
            setLoading(false);
        }
    }, [user, api]);

    // Single request to the cross-community flagged endpoint. The server
    // returns posts from every community the requester moderates, enriched
    // with communityName per post and sorted newest-first, so the client
    // doesn't need to fan out, merge, or re-sort. The early return on
    // empty moderator memberships saves a guaranteed-empty round-trip.
    const fetchFlagged = useCallback(async () => {
        // Clear any stale error before the early-return decision. Without
        // this, a prior failed fetch's flaggedError stays on screen if
        // the user then loses their last moderator membership (admin
        // demote, community archive) and fetchFlagged re-fires into the
        // early return — the dismissible "Try Again" button calls
        // fetchFlagged again, hits the same early return, and the error
        // never clears.
        setFlaggedError(null);
        if (!user || moderatorCommunities.length === 0) {
            setFlaggedPosts([]);
            setFlaggedTotal(0);
            setLoadingFlagged(false);
            return;
        }
        // Single-round-trip design: the cross-community /api/posts/flagged
        // endpoint returns posts + total across every community the user
        // moderates in one call. Trade-off vs. the pre-PR per-community
        // fan-out: a single failure here (transient DB error, populate
        // cast issue) blanks the whole flagged section rather than
        // degrading to per-community partial results. The simpler client
        // and single round-trip are the wins; per-community resilience
        // would require either a server response shape with per-community
        // success/failure or going back to client fan-out.
        try {
            setLoadingFlagged(true);
            const data = await getFlaggedPostsForModerator(api);
            setFlaggedPosts(data.posts || []);
            setFlaggedTotal(data.total || 0);
        } catch (err) {
            console.error('Failed to fetch flagged posts:', err);
            setFlaggedError(
                err.response?.data?.message || 'Failed to load flagged content'
            );
        } finally {
            setLoadingFlagged(false);
        }
    }, [user, api, moderatorCommunities]);

    useEffect(() => {
        if (!communitiesLoading) {
            fetchReports();
            fetchFlagged();
        }
    }, [fetchReports, fetchFlagged, communitiesLoading]);

    const handleDismissAll = async (postId) => {
        setActionInProgress(postId);
        try {
            await dismissReportsByPost(api, postId);
            setReportedPosts((prev) =>
                prev.filter((item) => item._id !== postId)
            );
        } catch (err) {
            console.error('Failed to dismiss reports:', err);
            setError(
                err.response?.data?.message || 'Failed to dismiss reports'
            );
        } finally {
            setActionInProgress(null);
        }
    };

    const handleModerate = async () => {
        const { postId, action } = confirmModal;
        setConfirmModal({
            show: false,
            postId: null,
            action: null,
            postTitle: '',
        });
        setActionInProgress(postId);
        try {
            await moderatePost(api, postId, action);
            // A post could be surfaced in either the reports list (user-
            // reported) or the flagged list (engine-flagged); remove from
            // whichever list it's in.
            setReportedPosts((prev) =>
                prev.filter((item) => item._id !== postId)
            );
            setFlaggedPosts((prev) =>
                prev.filter((item) => item._id !== postId)
            );
        } catch (err) {
            console.error('Failed to moderate post:', err);
            setError(err.response?.data?.message || 'Failed to moderate post');
        } finally {
            setActionInProgress(null);
        }
    };

    const openConfirmModal = (postId, action, postTitle) => {
        setConfirmModal({ show: true, postId, action, postTitle });
    };

    const closeConfirmModal = () => {
        setConfirmModal({
            show: false,
            postId: null,
            action: null,
            postTitle: '',
        });
    };

    if (loading || communitiesLoading) {
        return (
            <main className="py-4" aria-label="Moderation queue">
                <Container>
                    <div className="d-flex justify-content-center p-5">
                        <Spinner animation="border" variant="light" />
                        <span className="visually-hidden">
                            Loading reports...
                        </span>
                    </div>
                </Container>
            </main>
        );
    }

    if (moderatorCommunities.length === 0) {
        return (
            <main className="py-4" aria-label="Moderation queue">
                <Container>
                    <Alert variant="warning">
                        You are not currently a moderator of any communities.
                    </Alert>
                </Container>
            </main>
        );
    }

    return (
        <main className="py-4" aria-label="Moderation queue">
            <Container>
                <div className="d-flex align-items-center gap-3 mb-4">
                    <h2 className="fs-3 fw-bold text-white mb-0">Moderation</h2>
                    {/* Outside the h2 so the long aria-label is not folded
                        into the heading's accessible name. Dismiss is the only
                        action with no confirmation step. */}
                    <InfoTooltip
                        variant="light"
                        text="Dismiss clears every report on a post and leaves the post published. Hide and Delete ask you to confirm and explain what they do before they take effect."
                    />
                    {reportedPosts.length > 0 && (
                        <Badge bg="danger" pill className="fs-6">
                            {reportedPosts.length}
                        </Badge>
                    )}
                </div>

                {error && (
                    <Alert
                        variant="danger"
                        dismissible
                        onClose={() => setError(null)}
                    >
                        <Alert.Heading>Something went wrong</Alert.Heading>
                        <p className="mb-2">{error}</p>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={fetchReports}
                        >
                            Try Again
                        </Button>
                    </Alert>
                )}

                {!error && reportedPosts.length === 0 && (
                    <Alert variant="info">No pending reports. All clear!</Alert>
                )}

                {reportedPosts.map((item) => (
                    <Card key={item._id} className="mb-3 shadow-sm">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="flex-grow-1 me-3">
                                    <h3 className="fs-5 fw-semibold mb-1">
                                        {item.post?.title || 'Untitled Post'}
                                    </h3>
                                    <p className="text-body-secondary mb-1 small">
                                        by{' '}
                                        <span className="fw-medium">
                                            {item.post?.authorName || 'Unknown'}
                                        </span>
                                        {item.post?.createdAt && (
                                            <>
                                                {' '}
                                                &middot;{' '}
                                                {formatDate(
                                                    item.post.createdAt
                                                )}
                                            </>
                                        )}
                                    </p>
                                </div>
                                <Badge bg="danger" className="fs-6">
                                    {item.reportCount}{' '}
                                    {item.reportCount === 1
                                        ? 'report'
                                        : 'reports'}
                                </Badge>
                            </div>

                            <p className="text-body mb-3">
                                {truncate(item.post?.content)}
                            </p>

                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {item.reasons.map((reason) => (
                                    <Badge
                                        key={reason}
                                        bg="warning"
                                        text="dark"
                                    >
                                        {reasonLabel(reason)}
                                    </Badge>
                                ))}
                            </div>

                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="mb-3"
                                onClick={() =>
                                    setExpandedPost(
                                        expandedPost === item._id
                                            ? null
                                            : item._id
                                    )
                                }
                                aria-expanded={expandedPost === item._id}
                                aria-controls={`reports-detail-${item._id}`}
                                aria-label={`${expandedPost === item._id ? 'Hide' : 'Show'} report details for ${item.post?.title || 'this post'}`}
                            >
                                {expandedPost === item._id
                                    ? 'Hide details'
                                    : 'Show report details'}
                            </Button>

                            <Collapse in={expandedPost === item._id}>
                                <div id={`reports-detail-${item._id}`}>
                                    <div className="border rounded p-3 bg-light mb-3">
                                        {item.reports.map((report) => (
                                            <div
                                                key={report._id}
                                                className="mb-3 pb-3 border-bottom"
                                            >
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <span className="fw-medium small">
                                                        {report.reporterName}
                                                    </span>
                                                    <span className="text-body-secondary small">
                                                        {formatDate(
                                                            report.createdAt
                                                        )}
                                                    </span>
                                                </div>
                                                <Badge
                                                    bg="warning"
                                                    text="dark"
                                                    className="mb-1"
                                                >
                                                    {reasonLabel(report.reason)}
                                                </Badge>
                                                {report.comments && (
                                                    <p className="text-body small mb-0 mt-1">
                                                        &ldquo;
                                                        {report.comments}
                                                        &rdquo;
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Collapse>

                            <div className="d-flex flex-wrap gap-2">
                                <Button
                                    variant="info"
                                    size="sm"
                                    className="text-white"
                                    disabled={actionInProgress === item._id}
                                    onClick={() => handleDismissAll(item._id)}
                                    aria-label={`Dismiss all reports for ${item.post?.title || 'this post'}`}
                                >
                                    {actionInProgress === item._id && (
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-1"
                                        />
                                    )}
                                    Dismiss
                                </Button>
                                <Button
                                    variant="warning"
                                    size="sm"
                                    className="text-dark"
                                    disabled={actionInProgress === item._id}
                                    onClick={() =>
                                        openConfirmModal(
                                            item._id,
                                            'hide',
                                            item.post?.title || 'Untitled Post'
                                        )
                                    }
                                    aria-label={`Hide ${item.post?.title || 'this post'}`}
                                >
                                    Hide Post
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    disabled={actionInProgress === item._id}
                                    onClick={() =>
                                        openConfirmModal(
                                            item._id,
                                            'delete',
                                            item.post?.title || 'Untitled Post'
                                        )
                                    }
                                    aria-label={`Delete ${item.post?.title || 'this post'}`}
                                >
                                    Delete Post
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                ))}

                <div className="d-flex align-items-center gap-3 mt-5 mb-4">
                    <h2 className="fs-3 fw-bold text-white mb-0">
                        Flagged content
                    </h2>
                    {/* The two badge states below mean different things; the
                        intro paragraph only names them collectively. */}
                    <InfoTooltip
                        variant="light"
                        text="Flagged by AI means the moderation engine reviewed the post and marked it.
                         Pending review means the engine was unreachable when the post was submitted, so nothing has checked it yet."
                    />
                    {flaggedTotal > 0 && (
                        <Badge bg="warning" text="dark" pill className="fs-6">
                            {flaggedTotal}
                        </Badge>
                    )}
                </div>
                <p className="text-white-50 small mb-4">
                    Posts the AI moderation engine flagged or held for review in
                    communities you moderate. Approval is reserved for site
                    admins; you can hide or delete from here.
                </p>
                {flaggedTotal > flaggedPosts.length && (
                    <Alert variant="info" className="py-2 small">
                        Showing the {flaggedPosts.length} most-recent flagged
                        posts of {flaggedTotal} total across your communities.
                        Action items as you go; the next page rolls in once
                        these are cleared.
                    </Alert>
                )}

                {loadingFlagged && (
                    <div className="d-flex justify-content-center p-4">
                        <Spinner animation="border" variant="light" />
                        <span className="visually-hidden">
                            Loading flagged content...
                        </span>
                    </div>
                )}

                {flaggedError && (
                    <Alert
                        variant="danger"
                        dismissible
                        onClose={() => setFlaggedError(null)}
                    >
                        <Alert.Heading>Something went wrong</Alert.Heading>
                        <p className="mb-2">{flaggedError}</p>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={fetchFlagged}
                        >
                            Try Again
                        </Button>
                    </Alert>
                )}

                {!loadingFlagged &&
                    !flaggedError &&
                    flaggedPosts.length === 0 && (
                        <Alert variant="info">
                            No flagged content right now.
                        </Alert>
                    )}

                {!loadingFlagged &&
                    flaggedPosts.map((item) => (
                        <Card
                            key={`flagged-${item._id}`}
                            className="mb-3 shadow-sm"
                        >
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="flex-grow-1 me-3">
                                        <h3 className="fs-5 fw-semibold mb-1">
                                            {item.title || 'Untitled Post'}
                                        </h3>
                                        <p className="text-body-secondary mb-1 small">
                                            by{' '}
                                            <span className="fw-medium">
                                                {item.authorName || 'Unknown'}
                                            </span>
                                            {item.createdAt && (
                                                <>
                                                    {' '}
                                                    &middot;{' '}
                                                    {formatDate(item.createdAt)}
                                                </>
                                            )}
                                            {item.communityName && (
                                                <>
                                                    {' '}
                                                    &middot; in{' '}
                                                    <span className="fw-medium">
                                                        {item.communityName}
                                                    </span>
                                                </>
                                            )}
                                        </p>
                                    </div>
                                    <Badge bg="warning" text="dark">
                                        {FLAGGED_STATUS_LABELS[item.status] ||
                                            item.status}
                                    </Badge>
                                </div>

                                <p className="text-body mb-3">
                                    {truncate(item.content)}
                                </p>

                                <div className="d-flex flex-wrap gap-2">
                                    <Button
                                        variant="warning"
                                        size="sm"
                                        className="text-dark"
                                        disabled={actionInProgress === item._id}
                                        onClick={() =>
                                            openConfirmModal(
                                                item._id,
                                                'hide',
                                                item.title || 'Untitled Post'
                                            )
                                        }
                                        aria-label={`Hide ${item.title || 'this post'}`}
                                    >
                                        Hide Post
                                    </Button>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        disabled={actionInProgress === item._id}
                                        onClick={() =>
                                            openConfirmModal(
                                                item._id,
                                                'delete',
                                                item.title || 'Untitled Post'
                                            )
                                        }
                                        aria-label={`Delete ${item.title || 'this post'}`}
                                    >
                                        Delete Post
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}

                <Modal
                    show={confirmModal.show}
                    onHide={closeConfirmModal}
                    centered
                    aria-labelledby="mod-confirm-modal-title"
                >
                    <Modal.Header closeButton>
                        <Modal.Title
                            id="mod-confirm-modal-title"
                            className="fs-5"
                        >
                            Confirm{' '}
                            {confirmModal.action === 'hide' ? 'Hide' : 'Delete'}{' '}
                            Post
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            Are you sure you want to{' '}
                            <strong>
                                {confirmModal.action === 'hide'
                                    ? 'hide'
                                    : 'permanently delete'}
                            </strong>{' '}
                            the post &ldquo;{confirmModal.postTitle}&rdquo;?
                        </p>
                        {confirmModal.action === 'hide' && (
                            <p className="text-body-secondary small mb-0">
                                Hidden posts are removed from public listings
                                but preserved in the database.
                            </p>
                        )}
                        {confirmModal.action === 'delete' && (
                            <p className="text-danger small mb-0">
                                The post is removed from the community and no
                                longer counts toward its post total.
                            </p>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="outline-dark"
                            onClick={closeConfirmModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant={
                                confirmModal.action === 'hide'
                                    ? 'warning'
                                    : 'danger'
                            }
                            disabled={!!actionInProgress}
                            onClick={handleModerate}
                        >
                            {confirmModal.action === 'hide'
                                ? 'Hide Post'
                                : 'Delete Post'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </main>
    );
}
